// src/components/telemetry/TelemetryCharts.jsx
// =============================================================================
// Funzioni di base della telemetria (distanze, formati) e stato del device,
// usati dalle viste di ALLENAMENTO. I grafici di analisi sono in
// TelemetryPro.jsx; PilotLive.jsx mantiene i suoi grafici per la vista gara.
// =============================================================================
import React from 'react';

/* ---------- Helpers geo/colore ---------- */
export const toRad = (d) => d * Math.PI / 180;
export const haversine = (lat1, lon1, lat2, lon2) => {
  const R = 6371000;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};
export const clamp = (v, a, b) => Math.max(a, Math.min(b, v));
export const lerp = (a, b, t) => a + (b - a) * t;

export const getCanvasColors = () => {
  if (typeof document === 'undefined') {
    return {
      bg: '#0a0a0e', grid: 'rgba(255,255,255,0.035)',
      track: 'rgba(80,84,90,0.95)', outline: 'rgba(255,255,255,0.10)',
      text: '#ffffff', textShadow: 'rgba(0,0,0,0.8)',
    };
  }
  const cs = getComputedStyle(document.documentElement);
  const read = (name, fallback) => (cs.getPropertyValue(name).trim() || fallback);
  return {
    bg:         read('--rs-canvas-bg',          '#0a0a0e'),
    grid:       read('--rs-canvas-grid',        'rgba(255,255,255,0.035)'),
    track:      read('--rs-canvas-track',       'rgba(80,84,90,0.95)'),
    outline:    read('--rs-canvas-outline',     'rgba(255,255,255,0.10)'),
    text:       read('--rs-canvas-text',        '#ffffff'),
    textShadow: read('--rs-canvas-text-shadow', 'rgba(0,0,0,0.8)'),
  };
};

export const colorToRgba = (color, alpha) => {
  if (color.startsWith('#')) {
    const hex = color.slice(1);
    const full = hex.length === 3 ? hex.split('').map(c => c + c).join('') : hex;
    const v = parseInt(full, 16);
    const r = (v >> 16) & 255, g = (v >> 8) & 255, b = v & 255;
    return `rgba(${r},${g},${b},${alpha})`;
  }
  if (color.startsWith('rgba')) return color.replace(/[\d.]+\)$/, `${alpha})`);
  if (color.startsWith('rgb')) return color.replace('rgb', 'rgba').replace(')', `,${alpha})`);
  return color;
};

export const formatLap = (s) => {
  if (!s && s !== 0) return '—';
  const m = Math.floor(s / 60);
  const sec = (s % 60).toFixed(3);
  return `${m}:${sec.padStart(6, '0')}`;
};
export const formatDelta = (s) => {
  if (s == null) return '—';
  const sign = s >= 0 ? '+' : '−';
  const v = Math.abs(s);
  return `${sign}${v.toFixed(3)}`;
};

export const findDistOnCircuit = (lat, lon, sectors) => {
  if (!sectors || sectors.length < 2) return 0;
  let bestDist = Infinity;
  let bestCum = 0;
  let cum = 0;
  for (let i = 0; i < sectors.length; i++) {
    if (i > 0) cum += haversine(sectors[i - 1].lat, sectors[i - 1].lon, sectors[i].lat, sectors[i].lon);
    const d = haversine(lat, lon, sectors[i].lat, sectors[i].lon);
    if (d < bestDist) { bestDist = d; bestCum = cum; }
  }
  return bestCum;
};

export const findTrailPointAtDist = (trail, targetDist) => {
  if (!trail || trail.length === 0) return null;
  let lo = 0, hi = trail.length - 1;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (trail[mid].dist < targetDist) lo = mid + 1;
    else hi = mid;
  }
  if (lo > 0 && Math.abs(trail[lo - 1].dist - targetDist) < Math.abs(trail[lo].dist - targetDist)) {
    return trail[lo - 1];
  }
  return trail[lo];
};

/* ═══════════════ DEVICE STATUS BAR ═══════════════ */
function tempColor(t) {
  if (t == null) return '#555';
  if (t >= 75) return '#f44336';
  if (t >= 60) return '#ff9800';
  return '#4caf50';
}

export function DeviceStatusBar({ battery, signal4g, sats, mcuTemp, modemTemp, hr, beaconId, hrCaption }) {
  const hrColor = hr == null ? '#555' : hr >= 170 ? '#f44336' : hr >= 150 ? '#ff9800' : '#e91e63';
  const hrLabel = hr != null ? `${hr} bpm` : beaconId ? 'in attesa' : '—';
  const batteryColor = battery == null ? '#555' : battery > 60 ? '#4caf50' : battery > 25 ? '#ff9800' : '#f44336';
  const signalBars = signal4g != null ? Math.round((signal4g / 100) * 4) : 0;
  const mcuColor = tempColor(mcuTemp);
  const modemColor = tempColor(modemTemp);
  const signalColor = signal4g == null ? '#555' : signal4g > 50 ? '#4caf50' : signal4g > 20 ? '#ff9800' : '#f44336';
  const batteryLabel = battery == null ? 'N/D' : battery > 60 ? 'Buona' : battery > 25 ? 'Media' : 'Critica';
  const signalLabel = signal4g == null ? 'N/D' : signal4g > 50 ? 'Stabile' : signal4g > 20 ? 'Debole' : 'Critico';
  const satsLabel = sats == null ? 'Satelliti non disponibili' : sats >= 8 ? 'Fix GPS solido' : sats >= 4 ? 'Fix GPS sufficiente' : 'Fix GPS debole';

  return (
    <div className="rs-device-status">
      <div className="rs-device-primary" style={{ '--metric-color': batteryColor }}>
        <div className="rs-device-primary-main">
          <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="1" y="6" width="18" height="12" rx="2" />
            <line x1="23" y1="10" x2="23" y2="14" />
          </svg>
          <div className="rs-device-primary-text">
            <span>Batteria</span>
            <small>{batteryLabel}</small>
          </div>
          <strong>{battery != null ? `${battery}%` : '—'}</strong>
        </div>
        <div className="rs-device-bar-fill">
          <div className="rs-device-bar-fill-inner" style={{ width: `${battery ?? 0}%` }} />
        </div>
      </div>

      <div className="rs-device-primary" style={{ '--metric-color': signalColor }}>
        <div className="rs-device-primary-main">
          <svg width="26" height="26" viewBox="0 0 24 24" fill="none">
            {[0, 1, 2, 3].map(i => (
              <rect key={i} x={2 + i * 6} y={18 - (i + 1) * 4} width="4" height={(i + 1) * 4}
                rx="1" fill={i < signalBars ? 'currentColor' : 'currentColor'} fillOpacity={i < signalBars ? 1 : 0.22} />
            ))}
          </svg>
          <div className="rs-device-primary-text">
            <span>Rete 4G</span>
            <small>{signalLabel}</small>
          </div>
          <strong>{signal4g != null ? `${signal4g}%` : '—'}</strong>
        </div>
        <div className="rs-device-bar-fill">
          <div className="rs-device-bar-fill-inner" style={{ width: `${signal4g ?? 0}%` }} />
        </div>
      </div>

      <div className="rs-device-secondary-grid">
        <div className="rs-device-secondary" style={{ '--metric-color': mcuColor }} title="Temperatura MCU (ESP32)">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M14 14.76V3.5a2.5 2.5 0 0 0-5 0v11.26a4.5 4.5 0 1 0 5 0z" />
          </svg>
          <div>
            <span>MCU</span>
            <strong>{mcuTemp != null ? `${mcuTemp.toFixed(1)}°C` : '—'}</strong>
          </div>
        </div>

        <div className="rs-device-secondary" style={{ '--metric-color': modemColor }} title="Temperatura modem 4G (A7670E)">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M2 12h2M20 12h2M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" />
            <circle cx="12" cy="12" r="4" />
          </svg>
          <div>
            <span>Modem</span>
            <strong>{modemTemp != null ? `${modemTemp.toFixed(1)}°C` : '—'}</strong>
          </div>
        </div>

        <div className="rs-device-secondary rs-device-secondary--gps" style={{ '--metric-color': '#C0FF03' }}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="3" />
            <path d="M12 1v4M12 19v4M1 12h4M19 12h4" />
          </svg>
          <div>
            <span>GPS</span>
            <strong>{sats ?? '—'} sat</strong>
          </div>
        </div>

        <div className="rs-device-secondary" style={{ '--metric-color': hrColor }} title={beaconId ? `Fascia cardio BLE #${beaconId}` : 'Nessuna fascia cardio agganciata'}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.6l-1-1a5.5 5.5 0 0 0-7.8 7.8l1 1L12 21l7.8-7.6 1-1a5.5 5.5 0 0 0 0-7.8z" />
          </svg>
          <div>
            <span>Cardio{hrCaption ? ` · ${hrCaption}` : ''}</span>
            <strong>{hrLabel}</strong>
          </div>
        </div>
      </div>

      <div className="rs-device-footnote">{satsLabel}</div>
    </div>
  );
}
